import React, { useState, useEffect, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { rlApiService, listTopModelsSummary, getModelTrades, deleteEvolutionaryExperiment } from '../../api/rlApi';
import './RLModels.css';

function ModelDetailsModal({ model, onClose }) {
  const [detailedMetrics, setDetailedMetrics] = useState(null);
  const [tradeHistory, setTradeHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('metrics');

  useEffect(() => {
    loadDetailedData();
  }, [model.id]);

  const loadDetailedData = async () => {
    try {
      setLoading(true);
      const [metricsData, tradesData] = await Promise.all([
        rlApiService.getModel(model.id),
        getModelTrades(model.id).catch(() => ({ trades: [], eval_trades: [], summary: {}, eval_summary: {} })),
      ]);

      setDetailedMetrics(metricsData);
      setTradeHistory({
        trades: tradesData.trades || [],
        eval_trades: tradesData.eval_trades || [],
        performance_summary: tradesData.summary || {},
      });
    } catch (err) {
      console.error('Error loading detailed data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Loading {model.name} Details...</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📊 {model.name} - Detailed Analysis</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'metrics' ? 'active' : ''}`}
            onClick={() => setActiveTab('metrics')}
          >
            📈 Performance Metrics
          </button>
          <button
            className={`tab-btn ${activeTab === 'trades' ? 'active' : ''}`}
            onClick={() => setActiveTab('trades')}
          >
            💼 Trade History ({tradeHistory?.total_trades || 0})
          </button>
          <button
            className={`tab-btn ${activeTab === 'evolution' ? 'active' : ''}`}
            onClick={() => setActiveTab('evolution')}
          >
            🧬 Evolutionary Insights
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'metrics' && detailedMetrics && (
            <div className="metrics-grid">
              <div className="metric-section">
                <h4>🎯 Core Performance</h4>
                <div className="metric-cards">
                  <div className="metric-card">
                    <span className="metric-label">Sharpe Ratio</span>
                    <span className="metric-value">{detailedMetrics.performance_metrics?.sharpe_ratio !== undefined ? detailedMetrics.performance_metrics.sharpe_ratio.toFixed(3) : 'N/A'}</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Win Rate</span>
                    <span className="metric-value">{((detailedMetrics.performance_metrics?.win_rate || 0) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Total Return</span>
                    <span className="metric-value">{((detailedMetrics.performance_metrics?.total_return || 0) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Max Drawdown</span>
                    <span className="metric-value">{((detailedMetrics.performance_metrics?.max_drawdown || 0) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className="metric-section">
                <h4>⚡ Training Details</h4>
                <div className="metric-cards">
                  <div className="metric-card">
                    <span className="metric-label">Training Duration</span>
                    <span className="metric-value">
                      {detailedMetrics.performance_metrics?.training_duration_hours !== undefined
                        ? (detailedMetrics.performance_metrics.training_duration_hours / 3600).toFixed(1) + 'h'
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Data Points Used</span>
                    <span className="metric-value">{detailedMetrics.performance_metrics?.data_points_used?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Symbols Trained On</span>
                    <span className="metric-value">{detailedMetrics.performance_metrics?.symbols_traded?.length || 0}</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Indicators Used</span>
                    <span className="metric-value">{detailedMetrics.performance_metrics?.indicators_used?.length || 0}</span>
                  </div>
                </div>
              </div>

              <div className="metric-section">
                <h4>🎲 Risk Metrics</h4>
                <div className="metric-cards">
                  <div className="metric-card">
                    <span className="metric-label">Sortino Ratio</span>
                    <span className="metric-value">
                      {detailedMetrics.risk_metrics?.sortino_ratio !== undefined
                        ? detailedMetrics.risk_metrics.sortino_ratio.toFixed(3)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Calmar Ratio</span>
                    <span className="metric-value">
                      {detailedMetrics.risk_metrics?.calmar_ratio !== undefined
                        ? detailedMetrics.risk_metrics.calmar_ratio.toFixed(3)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Volatility</span>
                    <span className="metric-value">{((detailedMetrics.risk_metrics?.volatility || 0) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Robustness Score</span>
                    <span className="metric-value">{((detailedMetrics.model_characteristics?.robustness_score || 0) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trades' && tradeHistory && (
            <div className="trades-section">
              <div className="trade-summary">
                <h4>📊 Trade Summary</h4>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-label">Total Trades</span>
                    <span className="stat-value">
                      {tradeHistory.performance_summary?.total_trades ??
                        tradeHistory.total_trades ??
                        (tradeHistory.trades?.length ?? 0)}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Win Rate</span>
                    <span className="stat-value">
                      {tradeHistory.performance_summary?.win_rate !== undefined
                        ? `${(tradeHistory.performance_summary.win_rate * 100).toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Total P&L</span>
                    <span className="stat-value">
                      ${tradeHistory.performance_summary?.total_pnl?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Avg Trade P&L</span>
                    <span className="stat-value">
                      ${tradeHistory.performance_summary?.avg_trade_pnl?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="recent-trades">
                <h4>💼 Recent Trades</h4>
                <div className="trades-table-container">
                  <table className="trades-table">
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Side</th>
                        <th>Amount</th>
                        <th>Price</th>
                        <th>P&L</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeHistory.trades.slice(0, 20).map((trade, index) => (
                        <tr key={index}>
                          <td>{trade.symbol || 'N/A'}</td>
                          <td className={trade.side === 'buy' ? 'buy-side' : 'sell-side'}>
                            {trade.side?.toUpperCase() || 'N/A'}
                          </td>
                          <td>{trade.amount?.toFixed(6) || 'N/A'}</td>
                          <td>${trade.price?.toFixed(2) || 'N/A'}</td>
                          <td className={trade.pnl > 0 ? 'positive-pnl' : trade.pnl < 0 ? 'negative-pnl' : ''}>
                            ${trade.pnl?.toFixed(2) || '0.00'}
                          </td>
                          <td>{trade.timestamp ? new Date(trade.timestamp * 1000).toLocaleString() : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'evolution' && detailedMetrics && (
            <div className="evolution-section">
              <div className="evolution-insights">
                <h4>🧬 Evolutionary Insights</h4>
                {detailedMetrics.evolutionary_insights.parameter_importance && (
                  <div className="parameter-importance">
                    <h5>Parameter Importance</h5>
                    {Object.entries(detailedMetrics.evolutionary_insights.parameter_importance).map(([param, importance]) => (
                      <div key={param} className="param-item">
                        <span className="param-name">{param}</span>
                        <span className={`param-importance ${importance.toLowerCase()}`}>
                          {importance}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {detailedMetrics.evolutionary_insights.convergence_analysis && (
                  <div className="convergence-analysis">
                    <h5>Convergence Analysis</h5>
                    <div className="convergence-metrics">
                      <div className="metric">
                        <span className="label">Initial Fitness</span>
                        <span className="value">{detailedMetrics.evolutionary_insights.convergence_analysis.initial_fitness?.toFixed(3) || 'N/A'}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Final Fitness</span>
                        <span className="value">{detailedMetrics.evolutionary_insights.convergence_analysis.final_fitness?.toFixed(3) || 'N/A'}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Improvement</span>
                        <span className="value">+{detailedMetrics.evolutionary_insights.convergence_analysis.improvement?.toFixed(3) || 'N/A'}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Convergence Rate</span>
                        <span className="value">{(detailedMetrics.evolutionary_insights.convergence_analysis.convergence_rate * 100)?.toFixed(2) || 'N/A'}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {detailedMetrics.generation_history && detailedMetrics.generation_history.length > 0 && (
                <div className="generation-chart">
                  <h4>📈 Generation Evolution</h4>
                  <div className="generation-history">
                    {detailedMetrics.generation_history.slice(-10).map((gen, index) => (
                      <div key={index} className="generation-item">
                        <div className="gen-info">
                          <span className="gen-number">Gen {gen.generation}</span>
                          <span className="gen-fitness">Best: {gen.best_fitness?.toFixed(3) || 'N/A'}</span>
                          <span className="gen-mean">Mean: {gen.mean_fitness?.toFixed(3) || 'N/A'}</span>
                        </div>
                        <div className="gen-bar">
                          <div
                            className="fitness-bar"
                            style={{ width: `${Math.min((gen.best_fitness / 3) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RLModels() {
  const [models, setModels] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [totalModels, setTotalModels] = useState(0);
  const [experimentsWarning, setExperimentsWarning] = useState(null);
  const [modelsWarning, setModelsWarning] = useState(null);
  const [deletingExperiment, setDeletingExperiment] = useState(false);
  const [filters, setFilters] = useState({
    minSharpe: '',
    maxDrawdown: '',
    deployedOnly: false,
    search: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const withTimeout = (promise, label, ms = 20000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timed out loading ${label}`)), ms)
      ),
    ]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setExperimentsWarning(null);
      setModelsWarning(null);
      setSuccess(null);
      const withRetry = async (fn, attempts = 2) => {
        let lastErr;
        for (let i = 0; i < attempts; i++) {
          try {
            return await fn();
          } catch (err) {
            lastErr = err;
            if (i === attempts - 1) throw err;
          }
        }
        throw lastErr;
      };

      // Prefer full models list (plan-filtered); use top summary only as fallback
      const [modelsRes, experimentsRes, topSummaryRes] = await Promise.allSettled([
        withRetry(() => withTimeout(rlApiService.listModels(0), 'models', 35000), 3),
        withTimeout(rlApiService.listEvolutionaryExperiments(), 'experiments', 12000),
        withTimeout(listTopModelsSummary(), 'top models summary', 8000),
      ]);

      if (modelsRes.status === 'fulfilled') {
        setError(null); // ensure stale errors are cleared on success
        const list = modelsRes.value.models || [];
        setModels(list);
        setTotalModels(modelsRes.value.total || list.length);
        setModelsWarning(null);
      } else if (topSummaryRes.status === 'fulfilled') {
        const summary = topSummaryRes.value.models || [];
        setModels(
          summary.map((m) => ({
            ...m,
            performance_metrics: m.performance || m.performance_metrics || {},
          }))
        );
        setTotalModels(topSummaryRes.value.count || summary.length);
        setModelsWarning('Showing top summary only; full list unavailable right now');
      } else {
        if (models.length > 0) {
          setModelsWarning(
            modelsRes.reason?.message ||
              topSummaryRes.reason?.message ||
              'Unable to refresh models right now (showing cached list)'
          );
        } else {
          throw modelsRes.reason || topSummaryRes.reason || new Error('Failed to load models');
        }
      }

      if (experimentsRes.status === 'fulfilled') {
        setExperiments(experimentsRes.value.experiments || []);
      } else {
        setExperiments([]);
        setExperimentsWarning(experimentsRes.reason?.message || 'Unable to load experiments right now');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load models and experiments');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeployModel = async (modelId) => {
    try {
      setDeploying(modelId);
      setError(null);
      setSuccess(null);

      const result = await rlApiService.deployModel(modelId);

      setSuccess(`Model ${modelId} deployed successfully!`);
      await loadData(); // Refresh data
    } catch (err) {
      setError(`Failed to deploy model ${modelId}`);
      console.error('Error deploying model:', err);
    } finally {
      setDeploying(null);
    }
  };

  const handleDeleteExperiment = async (experimentId) => {
    if (!experimentId) return;
    const confirmDelete = window.confirm(`Delete experiment ${experimentId} and its models?`);
    if (!confirmDelete) return;
    try {
      setDeletingExperiment(true);
      setError(null);
      setSuccess(null);
      await deleteEvolutionaryExperiment(experimentId);
      await loadData();
      setSelectedExperiment(null);
    } catch (err) {
      setError(`Failed to delete experiment ${experimentId}`);
      console.error('Error deleting experiment:', err);
    } finally {
      setDeletingExperiment(false);
    }
  };

  const handleDeployBestModel = async () => {
    try {
      setDeploying('best');
      setError(null);
      setSuccess(null);

      const result = await rlApiService.deployBestModel();

      setSuccess(`Best model (${result.model_id}) deployed successfully!`);
      await loadData(); // Refresh data
    } catch (err) {
      setError('Failed to deploy best model');
      console.error('Error deploying best model:', err);
    } finally {
      setDeploying(null);
    }
  };

  const handleViewModelDetails = (model) => {
    setSelectedModel(model);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedModel(null);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const truncate = (str, max = 48) => {
    if (!str) return '—';
    return str.length > max ? `${str.slice(0, max - 1)}…` : str;
  };

  const filteredModels = useMemo(() => {
    return models.filter((m) => {
      if (filters.deployedOnly && !m.is_deployed) return false;
      if (filters.search && !m.name?.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.minSharpe !== '' && (m.performance_metrics.sharpe_ratio || 0) < Number(filters.minSharpe)) return false;
      if (filters.maxDrawdown !== '' && (m.performance_metrics.max_drawdown || 0) > Number(filters.maxDrawdown)) return false;
      return true;
    });
  }, [models, filters]);

  const paginatedModels = useMemo(() => {
    const pageSize = 24;
    return filteredModels.slice(0, pageSize);
  }, [filteredModels]);

  // Do not early-return on loading; render base UI with skeletons instead

  return (
    <div className="rl-section">

      {error && models.length === 0 && (
        <div className="rl-alert rl-alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="rl-alert rl-alert-success">
          <span className="alert-icon">✅</span>
          {success}
        </div>
      )}

      {/* Quick Actions */}
      <div className="rl-card">
        <h3>🚀 Quick Actions</h3>
        <div className="rl-actions-grid">
          <button
            className="rl-btn rl-btn-primary"
            onClick={handleDeployBestModel}
            disabled={deploying === 'best'}
          >
            {deploying === 'best' ? '🚀 Deploying...' : '🎯 Deploy Best Model'}
          </button>
          <button
            className="rl-btn rl-btn-secondary"
            onClick={() => window.open('/api/rl/export-report', '_blank')}
          >
            📤 Export Report
          </button>
          <button
            className="rl-btn rl-btn-secondary"
            onClick={() => window.location.href = '#/rl-agent/comparison'}
          >
            ⚖️ Compare with ML
          </button>
        </div>
      </div>

      {/* Available Models */}
      <div className="rl-card">
            <h3>📋 Available Models</h3>
            {totalModels > paginatedModels.length && (
              <p className="rl-note">
                Showing first {paginatedModels.length} of {totalModels} models (cached, limited).
              </p>
            )}
            {modelsWarning && (
              <div className="rl-alert rl-alert-warning">
                <span className="alert-icon">⚠️</span>
                {modelsWarning}
              </div>
            )}
            <div className="rl-filters">
              <div className="filter-group">
                <label>Search</label>
                <input
                  type="text"
                  placeholder="Name contains..."
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                />
              </div>
              <div className="filter-group">
                <label>Sharpe ≥</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 1.0"
                  value={filters.minSharpe}
                  onChange={(e) => setFilters((f) => ({ ...f, minSharpe: e.target.value }))}
                />
              </div>
              <div className="filter-group">
                <label>Max Drawdown ≤ %</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 15"
                  value={filters.maxDrawdown}
                  onChange={(e) => setFilters((f) => ({ ...f, maxDrawdown: e.target.value }))}
                />
              </div>
              <div className="filter-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={filters.deployedOnly}
                    onChange={(e) => setFilters((f) => ({ ...f, deployedOnly: e.target.checked }))}
                  />
                  Deployed only
                </label>
              </div>
            </div>
            {loading && models.length === 0 ? (
              <div className="rl-models-grid">
                {Array.from({ length: 12 }).map((_, idx) => (
                  <div key={idx} className="rl-model-card skeleton">
                    <div className="skeleton-line short" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line short" />
                  </div>
                ))}
              </div>
            ) : models.length === 0 ? (
              <div className="rl-empty-state">
                <p>No models available yet. Train some models first!</p>
              </div>
            ) : (
              <div className="rl-models-grid">
                {paginatedModels.map((model) => (
                  <div key={model.id} className={`rl-model-card ${model.is_deployed ? 'deployed' : ''}`}>
                    <div className="model-header">
                      <Tooltip title={model.name || ''}>
                        <h4
                          style={{
                            margin: 0,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                          }}
                        >
                          {truncate(model.name, 48)}
                        </h4>
                      </Tooltip>
                      <span className={`model-status ${model.is_deployed ? 'active' : 'inactive'}`}>
                        {model.is_deployed ? '🟢 Active' : '⚪ Inactive'}
                      </span>
                    </div>

                    <div className="model-metrics">
                      <div className="metric">
                        <span className="metric-label">Sharpe Ratio</span>
                        <span className="metric-value">{model.performance_metrics.sharpe_ratio?.toFixed(2) || 'N/A'}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Win Rate</span>
                        <span className="metric-value">{formatPercentage(model.performance_metrics.win_rate || 0)}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Total Return</span>
                        <span className="metric-value">{formatPercentage(model.performance_metrics.total_return || 0)}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Max Drawdown</span>
                        <span className="metric-value">{formatPercentage(model.performance_metrics.max_drawdown || 0)}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Created</span>
                        <span className="metric-value">{formatDate(model.created_at)}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Algorithm</span>
                        <span className="metric-value">{model.algorithm}</span>
                      </div>
                    </div>

                    <div className="model-info">
                      <p><strong>Algorithm:</strong> {model.algorithm}</p>
                      <p><strong>Created:</strong> {formatDate(model.created_at)}</p>
                    </div>

                    <div className="model-actions">
                      <button
                        className="rl-btn rl-btn-xs rl-btn-secondary"
                        onClick={() => handleViewModelDetails(model)}
                      >
                        📊 Details
                      </button>
                      {!model.is_deployed && (
                        <button
                          className="rl-btn rl-btn-sm rl-btn-primary"
                          onClick={() => handleDeployModel(model.id)}
                          disabled={deploying === model.id}
                        >
                          {deploying === model.id ? '🚀 Deploying...' : '🚀 Deploy'}
                        </button>
                      )}
                      {model.is_deployed && (
                        <span className="deployed-badge">🎯 Currently Active</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
      </div>

      {/* Evolutionary Experiments */}
      <div className="rl-card">
        <h3>🧬 Evolutionary Experiments</h3>
        {experimentsWarning && (
          <div className="rl-alert rl-alert-warning">
            <span className="alert-icon">⚠️</span>
            {experimentsWarning}
          </div>
        )}
        {experiments.length === 0 ? (
          <div className="rl-empty-state">
            <p>No evolutionary experiments found.</p>
          </div>
        ) : (
          <div className="rl-experiments-table">
            <table className="rl-table">
              <thead>
                <tr>
                  <th>Experiment ID</th>
                  <th>Status</th>
                  <th>Generations</th>
                  <th>Best Fitness</th>
                  <th>Population Size</th>
                  <th>Completed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {experiments.map((exp) => (
                  <tr key={exp.experiment_id}>
                    <td className="experiment-id">{exp.experiment_id}</td>
                    <td>
                      <span className={`status-badge ${exp.status}`}>
                        {exp.status === 'active' ? '🔄 Active' :
                         exp.status === 'completed' ? '✅ Completed' : '⏸️ Paused'}
                      </span>
                    </td>
                    <td>{exp.config?.generations || 'N/A'}</td>
                    <td>{exp.performance_metrics?.sharpe_ratio?.toFixed(3) || 'N/A'}</td>
                    <td>{exp.config?.population_size || 'N/A'}</td>
                    <td>{exp.completion_time ? formatDate(exp.completion_time) : 'N/A'}</td>
                    <td>
                      <button
                        className="rl-btn rl-btn-xs rl-btn-secondary"
                        onClick={() => window.location.href = `#/rl-agent/training?experiment=${exp.experiment_id}`}
                      >
                        📊 Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Model Performance Summary */}
      {models.length > 0 && (
        <div className="rl-card">
          <h3>📊 Performance Summary</h3>
          <div className="rl-performance-summary">
            <div className="summary-stat">
              <span className="stat-label">Total Models</span>
              <span className="stat-value">{models.length}</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Active Model</span>
              <Tooltip title={models.find(m => m.is_deployed)?.name || 'None'}>
                <span className="stat-value rl-truncate">
                  {truncate(models.find(m => m.is_deployed)?.name, 48) || 'None'}
                </span>
              </Tooltip>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Best Sharpe Ratio</span>
              <span className="stat-value">
                {Math.max(...models.map(m => m.performance_metrics.sharpe_ratio || 0)).toFixed(2)}
              </span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Avg Win Rate</span>
              <span className="stat-value">
                {formatPercentage(models.reduce((sum, m) => sum + (m.performance_metrics.win_rate || 0), 0) / models.length)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Model Details Modal */}
      {showModal && selectedModel && (
        <ModelDetailsModal
          model={selectedModel}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default RLModels;